import { useState } from 'react';
import Transition from 'react-tiny-transition';
import cn from 'classnames';

import useIsScreenSize from 'js/hooks/use-is-screen-size';
import useBlockScrolling from 'js/hooks/use-block-scrolling';

import Button from 'components/button/Button';
import { ContentWithSidebar, Content, Sidebar } from 'components/content-with-sidebar';
import ContentArea from 'components/content-area/ContentArea';
import ContentContainer from 'components/content-container/ContentContainer';
import Filters from 'components/filters/Filters';

import { IFilterLayout } from 'source/NFR.types';
import { SearchQueryParameter } from 'js/hooks/use-fetch-filtered-results';

export interface IFilterLayoutProps extends IFilterLayout {
  children: React.ReactNode;
  className?: string;
  topContent?: React.ReactNode;
  includeBottomBorder?: boolean;
  fetchFilteredResults: (searchQueryParameter: SearchQueryParameter) => void;
  isLoading?: boolean;
  updateFilterQuery?: (field: Record<string, (string | number)[]>) => void;
}

const FilterLayout: React.FC<IFilterLayoutProps> = ({
  children,
  contentArea,
  filters,
  topContent,
  includeBottomBorder = true,
  className,
  fetchFilteredResults,
  isLeft,
  isLoading,
  updateFilterQuery
}) => {
  const [mobileFiltersVisible, setMobileFiltersVisible] = useState(false);
  const isDesktop = useIsScreenSize('lg');

  useBlockScrolling(mobileFiltersVisible);
  return (
    <div className={cn('filter-layout', className)}>
      <div className="filter-layout--header">
        <ContentContainer theme="wide">
          <div
            className={cn('filter-layout--top-line', {
              'bottom-border': includeBottomBorder
            })}
          >
            {topContent}
          </div>
        </ContentContainer>
        <ContentWithSidebar theme={isLeft && isDesktop ? 'sidebarLeft' : undefined}>
          {filters && (
            <Button
              className="filter-layout--toggle"
              onClick={() => setMobileFiltersVisible(!mobileFiltersVisible)}
              icon={mobileFiltersVisible ? 'close-small' : 'filter-small'}
              iconFill
              text={filters.mobileTitle}
            />
          )}
        </ContentWithSidebar>
      </div>

      <Transition>
        {filters && mobileFiltersVisible && !isDesktop && (
          <div className="filter-layout--filters filter-layout--filters--is-mobile">
            <Filters
              {...filters}
              fetchFilteredResults={fetchFilteredResults}
              close={() => setMobileFiltersVisible(false)}
              updateFilterQuery={updateFilterQuery}
              isLoading={isLoading}
            />
          </div>
        )}
      </Transition>

      <ContentWithSidebar theme={isLeft && isDesktop ? 'sidebarLeft' : undefined}>
        <Content className="filter-layout--content">{children}</Content>

        <Sidebar>
          {filters && isDesktop && (
            <div className="filter-layout--filters filter-layout--filters--is-desktop">
              <Filters
                {...filters}
                fetchFilteredResults={fetchFilteredResults}
                updateFilterQuery={updateFilterQuery}
                close={() => undefined}
              />
            </div>
          )}
          {contentArea && <ContentArea {...contentArea} />}
        </Sidebar>
      </ContentWithSidebar>
    </div>
  );
};

FilterLayout.displayName = 'FilterLayout';

export default FilterLayout;
